

export const dynamic = "force-dynamic";

import { useState, useEffect } from "react";
import DashboardLayout from "@/app/components/DashboardLayout";
import { useInvoiceSettings } from "@/hooks/useInvoiceSettings";

const FORMAT_TOKENS = [
  { token: "{PREFIX}", description: "Invoice prefix (e.g., INV)" },
  { token: "{YEAR}", description: "Current year (2026)" },
  { token: "{MONTH}", description: "Current month (04)" },
  { token: "{NUMBER}", description: "Sequential number (00001)" },
];

export default function InvoiceSettingsPage() {
  const { settings, loading, preview, updateSettings, previewNextNumber } = useInvoiceSettings();
  
  const [formData, setFormData] = useState({
    prefix: "INV",
    format_pattern: "{PREFIX}-{YEAR}-{NUMBER}",
    number_padding: 5,
    reset_yearly: true,
    separator: "-",
    terms_and_conditions: "",
    footer_notes: "",
  });

  const [isSaving, setIsSaving] = useState(false);
  const [localPreview, setLocalPreview] = useState("");

  useEffect(() => {
    if (settings) {
      setFormData({
        prefix: settings.prefix,
        format_pattern: settings.format_pattern,
        number_padding: settings.number_padding,
        reset_yearly: settings.reset_yearly,
        separator: settings.separator,
        terms_and_conditions: settings.terms_and_conditions || "",
        footer_notes: settings.footer_notes || "",
      });
    }
  }, [settings]);

  // Update preview when form changes
  useEffect(() => {
    const mockSettings = {
      ...settings,
      ...formData,
      current_number: settings?.current_number || 0,
    };
    
    const year = new Date().getFullYear();
    const month = String(new Date().getMonth() + 1).padStart(2, '0');
    const nextNumber = mockSettings.current_number + 1;
    const number = String(nextNumber).padStart(mockSettings.number_padding, '0');
    
    let result = mockSettings.format_pattern
      .replace('{PREFIX}', mockSettings.prefix)
      .replace('{YEAR}', String(year))
      .replace('{MONTH}', month)
      .replace('{NUMBER}', number);
    
    setLocalPreview(result);
  }, [formData, settings]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    
    const success = await updateSettings(formData);
    if (success) {
      // Form already updated via hook
    }
    
    setIsSaving(false);
  };

  const insertToken = (token: string) => {
    setFormData(prev => ({
      ...prev,
      format_pattern: prev.format_pattern + token,
    }));
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="max-w-4xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-stone-200 rounded w-1/3"></div>
            <div className="h-96 bg-stone-200 rounded"></div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-stone-800">Invoice Settings</h1>
          <p className="text-sm text-stone-500 mt-1">
            Configure invoice number format and defaults
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Invoice Number Format Section */}
          <div className="bg-white rounded-xl border border-stone-200 p-6">
            <h2 className="text-lg font-semibold text-stone-800 mb-4">Invoice Number Format</h2>
            
            {/* Prefix */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-stone-700 mb-2">
                Prefix
              </label>
              <input
                type="text"
                value={formData.prefix}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, prefix: e.target.value })}
                placeholder="INV"
                maxLength={10}
                className="w-full max-w-xs px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
              <p className="text-xs text-stone-500 mt-1">
                Short code at the start of invoice numbers (e.g., INV, BILL, SI)
              </p>
            </div>

            {/* Format Pattern */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-stone-700 mb-2">
                Format Pattern
              </label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={formData.format_pattern}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, format_pattern: e.target.value })}
                  placeholder="{PREFIX}-{YEAR}-{NUMBER}"
                  className="flex-1 px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>
              
              {/* Available Tokens */}
              <div className="bg-stone-50 rounded-lg p-3 mb-2">
                <p className="text-xs font-medium text-stone-600 mb-2">Available Tokens:</p>
                <div className="flex flex-wrap gap-2">
                  {FORMAT_TOKENS.map((item) => (
                    <button
                      key={item.token}
                      type="button"
                      onClick={() => insertToken(item.token)}
                      className="px-2 py-1 bg-white border border-stone-300 rounded text-xs font-mono hover:bg-stone-100 transition-colors"
                      title={item.description}
                    >
                      {item.token}
                    </button>
                  ))}
                </div>
              </div>
              <p className="text-xs text-stone-500">
                Click tokens to add them to the pattern. Example: INV-2026-00001
              </p>
            </div>

            {/* Number Padding */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-stone-700 mb-2">
                Number Padding (digits)
              </label>
              <select
                value={formData.number_padding}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFormData({ ...formData, number_padding: parseInt(e.target.value) })}
                className="w-full max-w-xs px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
              >
                {[3, 4, 5, 6, 7, 8].map((num) => (
                  <option key={num} value={num}>
                    {num} digits (e.g., {String(1).padStart(num, '0')})
                  </option>
                ))}
              </select>
            </div>

            {/* Reset Yearly */}
            <div className="mb-4">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.reset_yearly}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, reset_yearly: e.target.checked })}
                  className="w-5 h-5 text-amber-600 border-stone-300 rounded focus:ring-amber-500"
                />
                <span className="text-sm font-medium text-stone-700">Reset number yearly</span>
              </label>
              <p className="text-xs text-stone-500 mt-1">
                When enabled, the sequence number resets to 00001 each year
              </p>
            </div>

            {/* Preview */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm font-medium text-amber-800 mb-1">Next Invoice Number Preview:</p>
              <p className="text-2xl font-bold text-amber-700 font-mono">
                {localPreview || preview || "INV-2026-00001"}
              </p>
            </div>
          </div>

          {/* Terms & Conditions */}
          <div className="bg-white rounded-xl border border-stone-200 p-6">
            <h2 className="text-lg font-semibold text-stone-800 mb-4">Default Terms & Conditions</h2>
            <textarea
              value={formData.terms_and_conditions}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setFormData({ ...formData, terms_and_conditions: e.target.value })}
              placeholder="Enter default terms and conditions for invoices..."
              rows={6}
              className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
            <p className="text-xs text-stone-500 mt-2">
              These terms will be automatically added to all new invoices
            </p>
          </div>

          {/* Footer Notes */}
          <div className="bg-white rounded-xl border border-stone-200 p-6">
            <h2 className="text-lg font-semibold text-stone-800 mb-4">Footer Notes</h2>
            <textarea
              value={formData.footer_notes}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setFormData({ ...formData, footer_notes: e.target.value })}
              placeholder="Enter footer notes (e.g., Thank you for your business)..."
              rows={3}
              className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
            <p className="text-xs text-stone-500 mt-2">
              Additional notes to display at the bottom of invoices
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => window.history.back()}
              className="px-6 py-2 border border-stone-300 rounded-lg text-stone-700 hover:bg-stone-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="px-6 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
            >
              {isSaving ? "Saving..." : "Save Settings"}
            </button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
}
